import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { track } from "@/lib/analytics";
import { BOOKS_SUPPORT, findChinnuProduct, isPrintPurchasable } from "@/data/books";
import { createPhysicalOrder, verifyPhysicalOrder } from "@/lib/physicalOrders";
import { loadRazorpayCheckout } from "@/components/books/BuyButton";
import DeliveryForm from "@/components/books/DeliveryForm";

const ACCENT = "#F57F17";

function money(paise) {
  return `₹${Math.round(paise / 100)}`;
}

/**
 * Chinnu physical-order flow — Command 04 §10/§11/§14.
 * Step 1: delivery form (client + server validated).
 * Step 2: review & confirm (consent required) → Razorpay Standard Checkout.
 * Step 3 (on success): redirect to the order-confirmed page with the
 * signed, order-scoped token the backend issued — never the frontend's
 * own idea of "paid".
 */
export default function ChinnuPhysicalCheckout() {
  const { printSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const product = findChinnuProduct(printSlug);

  const [step, setStep] = useState("form");
  const [delivery, setDelivery] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  const utm = useMemo(
    () => ({
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      utm_content: searchParams.get("utm_content") || undefined,
    }),
    [searchParams],
  );

  useSEO({
    title: product ? `Order ${product.title} — LifeIQ Chinnu` : "Order — LifeIQ Chinnu",
    description: "Order your printed Chinnu Talking Adventures book — secure delivery across India.",
    canonicalPath: `/books/chinnu/checkout/${printSlug}`,
  });

  if (!product || !isPrintPurchasable(product)) {
    return <Navigate to="/books/chinnu" replace />;
  }

  const price = product.print_price_value * 100;

  const handleFormContinue = (d) => {
    setDelivery(d);
    setStep("review");
    track("chinnu_shipping_details_complete", { product_id: product.print_product_id, city: d.city, state: d.state });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startPayment = async () => {
    if (!consent) {
      toast.error("Please confirm your delivery details and agree to the Terms and Privacy Policy.");
      return;
    }
    setPaymentError("");
    setLoading(true);
    track("chinnu_print_review_order", { product_id: product.print_product_id, amount: product.print_price_value });
    try {
      await loadRazorpayCheckout();
      const order = await createPhysicalOrder({
        product_id: product.print_product_id,
        quantity: 1,
        delivery,
        source_page: "chinnu-physical-checkout",
        consent: true,
        ...utm,
      });
      track("chinnu_print_checkout_created", { product_id: product.print_product_id, order_ref: order.order_ref, amount: order.amount });

      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        prefill: { name: delivery.full_name, email: delivery.email, contact: `+91${delivery.mobile.replace(/\D/g, "").slice(-10)}` },
        theme: { color: ACCENT },
        modal: {
          ondismiss: () => {
            setLoading(false);
            track("chinnu_print_payment_failed", { product_id: product.print_product_id, order_ref: order.order_ref, reason: "dismissed" });
          },
        },
        handler: async (response) => {
          try {
            const verify = await verifyPhysicalOrder({
              order_ref: order.order_ref,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verify.verified && verify.token) {
              track("chinnu_print_payment_success", { product_id: product.print_product_id, order_ref: verify.order_ref });
              track("chinnu_print_order_confirmed", { product_id: product.print_product_id, order_ref: verify.order_ref });
              navigate(`/books/chinnu/order-confirmed/${verify.order_ref}?token=${encodeURIComponent(verify.token)}`);
            } else {
              track("chinnu_print_payment_failed", { product_id: product.print_product_id, order_ref: order.order_ref, reason: verify.verification });
              setPaymentError("We couldn't verify your payment. If money was deducted, please contact LifeIQ support with your order reference before trying again.");
              setLoading(false);
            }
          } catch {
            setPaymentError("We couldn't verify your payment. Please contact LifeIQ support if money was deducted.");
            setLoading(false);
          }
        },
      });
      rzp.open();
    } catch (err) {
      setLoading(false);
      setPaymentError(err.message || "Could not start checkout. Please try again.");
    }
  };

  return (
    <main className="min-h-screen bg-[#FBF7EE]">
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <Link to={`/books/chinnu/${printSlug}`} data-testid="checkout-back-link" className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-wide font-semibold text-[#5C6470] hover:text-[#0D1B2A] transition-colors">
          <ArrowLeft size={13} /> Back
        </Link>

        <div className="mt-6 text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: ACCENT }}>Printed Edition Order</div>
        <h1 data-testid="checkout-title" className="mt-2 font-serif-d text-[28px] sm:text-[34px] text-[#0D1B2A] leading-[1.15]">{product.title}</h1>
        <p className="mt-1 text-[14px] text-[#5C6470]">Price: {product.print_price}</p>

        <div className="mt-3 flex items-center gap-2">
          {["form", "review"].map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span
                data-testid={`checkout-step-${s}`}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  step === s ? "text-white" : step === "review" && s === "form" ? "bg-[#004D40] text-white" : "bg-[#E5E7EA] text-[#5C6470]"
                }`}
                style={step === s ? { backgroundColor: ACCENT } : undefined}
              >
                {step === "review" && s === "form" ? <Check size={13} /> : i + 1}
              </span>
              <span className="text-[12px] uppercase tracking-wide font-semibold text-[#5C6470]">{s === "form" ? "Delivery Details" : "Review & Pay"}</span>
              {i === 0 && <span className="w-6 h-px bg-[#D9D4C4]" />}
            </span>
          ))}
        </div>

        <div className="mt-8 bg-white border border-[#E5E1D3] rounded-sm p-6 sm:p-8">
          {step === "form" && (
            <DeliveryForm initialValue={delivery} onContinue={handleFormContinue} accentColor={ACCENT} />
          )}

          {step === "review" && delivery && (
            <div data-testid="order-review-step">
              <div className="flex items-center justify-between">
                <h2 className="font-serif-d text-[20px] text-[#0D1B2A]">Review Your Order</h2>
                <button
                  type="button"
                  data-testid="checkout-edit-details-btn"
                  onClick={() => setStep("form")}
                  className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-wide font-semibold text-[#004D40] hover:underline"
                >
                  <Pencil size={12} /> Edit Details
                </button>
              </div>

              <div className="mt-5 space-y-2 text-[14px] text-[#0D1B2A]">
                <div className="flex justify-between"><span className="text-[#5C6470]">Product</span><span className="font-medium text-right">{product.title}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6470]">Quantity</span><span>1</span></div>
                <div className="flex justify-between"><span className="text-[#5C6470]">Book price</span><span>{money(price)}</span></div>
                <div className="flex justify-between"><span className="text-[#5C6470]">Additional shipping/handling</span><span>₹0</span></div>
                <div className="flex justify-between pt-2 border-t border-[#E5E7EA] font-semibold text-[16px]"><span>TOTAL</span><span data-testid="checkout-total">{money(price)}</span></div>
              </div>

              <div className="mt-5 pt-5 border-t border-[#E5E7EA]">
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#5C6470]">Deliver To</div>
                <p data-testid="checkout-delivery-summary" className="mt-1.5 text-[14px] text-[#0D1B2A] leading-[1.6]">
                  {delivery.full_name}<br />
                  {delivery.address_line1}, {delivery.address_line2}{delivery.landmark ? `, ${delivery.landmark}` : ""}<br />
                  {delivery.city}, {delivery.district ? `${delivery.district}, ` : ""}{delivery.state} {delivery.pin_code}<br />
                  {delivery.country} · {delivery.mobile} · {delivery.email}
                </p>
                {delivery.order_notes && <p className="mt-1.5 text-[13px] text-[#5C6470] italic">Note: {delivery.order_notes}</p>}
              </div>

              <p className="mt-5 text-[13px] text-[#5C6470] leading-[1.6]">
                We use your contact and delivery information to process, deliver and support your order. See our{" "}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>

              <label className="mt-4 flex items-start gap-2.5 text-[13px] text-[#0D1B2A] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="checkout-consent-checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5"
                />
                I confirm that my delivery address and contact details are correct and I agree to the Terms and Privacy Policy.
              </label>

              {paymentError && (
                <p data-testid="checkout-payment-error" className="mt-4 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
                  {paymentError}
                </p>
              )}

              <button
                type="button"
                data-testid="checkout-proceed-payment-btn"
                onClick={startPayment}
                disabled={loading || !consent}
                style={consent ? { backgroundColor: ACCENT } : undefined}
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase rounded-sm transition-opacity ${
                  consent ? "text-white hover:opacity-90 disabled:opacity-70" : "bg-[#E5E7EA] text-[#5C6470] cursor-not-allowed"
                }`}
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? "Opening secure checkout…" : `Proceed to Secure Payment — ${money(price)}`}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#5C6470]">
                <ShieldCheck size={12} style={{ color: ACCENT }} /> Secure payment through Razorpay
              </p>

              <p className="mt-6 text-center text-[12px] text-[#5C6470]">
                Need multiple copies for a school or group?{" "}
                <a href={`mailto:${BOOKS_SUPPORT.email}`} className="underline">Contact LifeIQ →</a>
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
